import { Boss, BossDef, BossAttack, SHOCKWAVE_MIN_SAFE_WARN_MS } from './boss';
import { GAME } from '../config';

/**
 * boss-roster-extra (boss 2026-06-13「boss 寫太少」): 6 隻全新「正式魔王」——
 * 從原本的「小魔王皮膚」升級成各自獨立 HP / 招式 / 二階段變身的真魔王,加進魔王池
 * 讓玩家在自由戰 boss 波真的會遭遇 (總 boss 數 9 → 15)。
 *
 * 鐵則對齊 (CLAUDE.md / .omc/wiki):
 *   - 純戰鬥內容,**0 戰力寫入** (不碰 factionXP/freeXP/skillLevels);
 *     魔王擊敗獎勵走既有 commanderXp (玩遊戲軌),由 scene 結算發放,這裡只描述招式。
 *   - learning-game-separation:戰鬥中絕不彈題,招式純機制。
 *   - K-3 可躲鐵則:每招都有「安全縫 / 預警」——
 *       fan-shot 中央留縫、ring-burst 雙層 + 缺口 + 緩旋、laser/beam 長預警、
 *       freeze/silence 都很短 (維持可玩,非長時間定身)。
 *   - boss-phase2-transform:每隻 phase2 (血量過半 berserk) 換一套「明顯不同」的招式,
 *     至少引入 1 個 phase1 沒有的攻擊 kind (主題化暴怒變身,不只是變快)。
 *
 * 實作風格刻意比照 boss-pool.ts tier1 / tier2 的「tickAttacks closure + cooldown」
 * (不走 boss-configs FSM),避免 boss-fsm-parity 的 byte-equal 鏡射地雷 (那只覆蓋
 * 既有 9 隻 ALL_LEGACY_BOSSES,新池不入鏡射)。
 *
 * 五門派分散 (affinityFaction):
 *   spider=elemental / hover=techno / train=dominion / drill=dominion /
 *   skull=celestial / crystal=runic — 玩家任一門派都有對應吃 ×1.5 的對手。
 */

// boss-tougher (boss 2026-06-13「魔王要夠強,太弱沒挑戰性」但仍要可躲):
//   這 6 隻「正式新魔王」皆 tier:2,不在 bosses.json(用本檔 TS 值上線)。HP 整批往上
//   (約 ×1.4,更肉)、cooldown 縮短(約 −15%,更兇/更密),phase2HpThreshold 抬到約 40% HP
//   (變身衝刺段更長)。可躲鐵則一律守住:招式種類不動、子彈速度不動、ring 速度 ≤170+雙層+
//   缺口 0.9、laser/beam warnMs 不動、freeze/silence ≤600。各 hp / size 仍兩兩相異(單測要求)。
//
// ── 共用安全參數 (K-3 可躲) ────────────────────────────────────────────────────
//
// ring-burst 進階大招參數,比照 boss-pool.ts RING_BERSERK_* (雙層 + 明顯缺口 +
// 緩旋,缺口恆在 → 衝缺口即安全)。在本檔內就地宣告,避免跨檔耦合。
const EXTRA_RING_LAYERS = 2;
const EXTRA_RING_GAP_RAD = 0.9;
const EXTRA_RING_ROTATE_DPS = 35;

// boss-aoe (老闆「魔王應具備…大面積傷害等能力」+「要有地方可以躲」):
// 衝擊波 (shockwave) 大面積大招的共用安全參數。warnMs 一律 1200ms (> 可躲門檻 1000ms),
// K-3 從容走出圈外即 0 傷;半徑刻意「不滿版」(≤ 圈外仍有大片安全地)。只給 phase2
// (berserk) 用 → 暴怒變身才釋放,增加「技能感」。圓心鎖 boss 位置 (不追玩家) → 一定躲得掉。
const EXTRA_SHOCKWAVE_WARN_MS = 1200;
const EXTRA_SHOCKWAVE_RADIUS = 230;
// 防呆:預警時間必須 ≥ 可躲門檻,否則 module load 即拋 (單測也可直接斷言這個不變式)。
if (EXTRA_SHOCKWAVE_WARN_MS < SHOCKWAVE_MIN_SAFE_WARN_MS) {
  throw new Error('shockwave warnMs below safe-dodge threshold');
}

/** 朝玩家的瞄準角 (場景以 π+angle remap 成朝左),三隻用同一套幾何。 */
function aimAngle(self: Boss, playerX: number): number {
  return Math.atan2(playerX - self.x, GAME.height * 0.85 - self.y);
}

// ── 蛛魔・八腳織 (spider) — 自然/元素:凍結 + 多向散射 → 散射加密 + 召小兵 ─────────
const SPIDER: BossDef = {
  id: 'spider',
  displayName: '蛛魔・八腳織',
  hp: 1250,
  speedPxPerSec: 48,
  size: 150,
  color: 0x2e7d32,
  assetKey: 'boss-spider',
  shape: 'hexagon',
  faction: 'elemental',
  affinityFaction: 'elemental',
  tier: 2,
  attackACooldownMs: 2200,
  attackBCooldownMs: 4400,
  phase2HpThreshold: 500,
  attackBWarningMs: 900,
  introDialog: '八腳之網,你逃不出蛛絲...',
  arenaTint: 0x0c1f0c,
  entranceCinematic: 'rise-from-ground',
  deathCinematic: 'fragment-into-pieces',
  uniqueGimmick: 'spawn-orbiting-minions',
  tickAttacks(self: Boss, nowMs: number, _playerX: number): BossAttack[] {
    // phase1:短凍結黏住 + 上下兩束多向散射 (中央留縫,慢速可閃)。
    // phase2 (berserk):散射加密成更寬扇 + 召小兵 (織網壓迫),spawn-minions 是 phase1 沒有的新 kind。
    const out: BossAttack[] = [];
    if (self.y < self.def.size / 2) return out;
    const berserk = self.berserkMode;
    if (nowMs - self.lastAttackAMs >= self.effectiveAttackACooldownMs) {
      self.lastAttackAMs = nowMs;
      out.push({ kind: 'freeze-player', durationMs: 220 });
      // 散射:上下各 3 顆,中央 [-0.2,0.2] 留縫,速度 260 (慢,看得出縫)。
      out.push({ kind: 'fan-shot', angles: [-0.8, -0.55, -0.3, 0.3, 0.55, 0.8], speedPxPerSec: 260, damage: 9 });
    }
    if (berserk && nowMs - self.lastAttackBMs >= self.effectiveAttackBCooldownMs) {
      self.lastAttackBMs = nowMs;
      // 暴怒:更寬更密的多向散射 (仍中央留縫) + 召兩隻小兵 (phase1 沒有 → 質變)。
      out.push({ kind: 'fan-shot', angles: [-1.0, -0.75, -0.5, -0.3, 0.3, 0.5, 0.75, 1.0], speedPxPerSec: 270, damage: 9 });
      out.push({ kind: 'spawn-minions', count: 2 });
    }
    return out;
  },
};

// ── 浮空・光掃者 (hover) — 機械:瞄準光束 + 追蹤 → 光束掃射 + 追蹤加量 ──────────────
const HOVER: BossDef = {
  id: 'hover',
  displayName: '浮空・光掃者',
  hp: 1180,
  speedPxPerSec: 60,
  size: 146,
  color: 0x00838f,
  assetKey: 'boss-hover',
  shape: 'double-circle',
  faction: 'techno',
  affinityFaction: 'techno',
  tier: 2,
  attackACooldownMs: 2400,
  attackBCooldownMs: 4600,
  phase2HpThreshold: 472,
  attackBWarningMs: 900,
  introDialog: '鎖定目標... 掃描範圍展開。',
  arenaTint: 0x0a1f22,
  entranceCinematic: 'teleport-in',
  deathCinematic: 'explode-standard',
  uniqueGimmick: 'teleport',
  tickAttacks(self: Boss, nowMs: number, playerX: number): BossAttack[] {
    // phase1:單發瞄準光束 (beam-cast,玩家側移即閃) + 少量追蹤波。
    // phase2 (berserk):雙道光束「掃射」(各偏一邊,站中縫安全) + 追蹤彈加量 (光束掃射主題)。
    //   laser-beam 是 phase1 沒有的新 kind (掃射用持續貫穿光束,長預警可躲)。
    const out: BossAttack[] = [];
    if (self.y < self.def.size / 2) return out;
    const berserk = self.berserkMode;
    if (nowMs - self.lastAttackAMs >= self.effectiveAttackACooldownMs) {
      self.lastAttackAMs = nowMs;
      out.push({ kind: 'beam-cast', angleRad: aimAngle(self, playerX), speedPxPerSec: 520, damage: 14 });
      out.push({ kind: 'homing-shot', count: 2, speedPxPerSec: 215, turnRatePerSec: 110, damage: 9 });
    }
    if (berserk && nowMs - self.lastAttackBMs >= self.effectiveAttackBCooldownMs) {
      self.lastAttackBMs = nowMs;
      // 掃射:兩道持續光束 (長預警 ≥600ms),各偏一邊,玩家站中央縫即安全。
      out.push({ kind: 'laser-beam', warnMs: 680, fireMs: 420, thickness: 22, damage: 14 });
      out.push({ kind: 'laser-beam', warnMs: 620, fireMs: 400, thickness: 20, damage: 12 });
      out.push({ kind: 'homing-shot', count: 4, speedPxPerSec: 230, turnRatePerSec: 130, damage: 9 });
    }
    return out;
  },
};

// ── 鐵甲・噴火車 (train) — 疆域:寬扇 + 單發 → 噴火彈幕 + 環彈 ──────────────────────
const TRAIN: BossDef = {
  id: 'train',
  displayName: '鐵甲・噴火車',
  hp: 1500,
  speedPxPerSec: 28,
  size: 170,
  color: 0xbf360c,
  assetKey: 'boss-train',
  shape: 'diamond',
  faction: 'dominion',
  affinityFaction: 'dominion',
  tier: 2,
  attackACooldownMs: 2600,
  attackBCooldownMs: 4800,
  phase2HpThreshold: 600,
  attackBWarningMs: 1000,
  introDialog: '蒸汽全開,輾過一切阻擋!',
  arenaTint: 0x2a0f06,
  entranceCinematic: 'descend',
  deathCinematic: 'explode-standard',
  uniqueGimmick: 'phase-shield',
  tickAttacks(self: Boss, nowMs: number, playerX: number): BossAttack[] {
    // phase1:寬扇齊射 (中央留縫) + 高速單發 (重砲)。
    // phase2 (berserk):噴火彈幕 — 更密的寬扇「火舌」 + 環形火環 (ring-burst 雙層+缺口)。
    //   ring-burst 是 phase1 沒有的新 kind (噴火主題的全方位火環,衝缺口可躲)。
    const out: BossAttack[] = [];
    if (self.y < self.def.size / 2) return out;
    const berserk = self.berserkMode;
    if (nowMs - self.lastAttackAMs >= self.effectiveAttackACooldownMs) {
      self.lastAttackAMs = nowMs;
      out.push({ kind: 'fan-shot', angles: [-0.7, -0.45, -0.25, 0.25, 0.45, 0.7], speedPxPerSec: 280, damage: 10 });
      out.push({ kind: 'single-shot', angleRad: aimAngle(self, playerX), speedPxPerSec: 400, damage: 18 });
    }
    if (berserk && nowMs - self.lastAttackBMs >= self.effectiveAttackBCooldownMs) {
      self.lastAttackBMs = nowMs;
      // 噴火彈幕:密集寬扇火舌 (中央留縫,慢速 250) + 大面積火爆 (shockwave AoE,預警圈可躲)
      // + 全向火環 (雙層+缺口+緩旋)。shockwave 是 phase1 沒有的新 kind (噴火車「引擎過熱大爆」)。
      out.push({ kind: 'fan-shot', angles: [-1.0, -0.78, -0.56, -0.34, 0.34, 0.56, 0.78, 1.0], speedPxPerSec: 250, damage: 9 });
      out.push({ kind: 'shockwave', radiusPx: EXTRA_SHOCKWAVE_RADIUS, warnMs: EXTRA_SHOCKWAVE_WARN_MS, damage: 15 });
      out.push({ kind: 'ring-burst', count: 18, speedPxPerSec: 150, damage: 8, layers: EXTRA_RING_LAYERS, gapRad: EXTRA_RING_GAP_RAD, rotateDps: EXTRA_RING_ROTATE_DPS });
    }
    return out;
  },
};

// ── 鑽地・震波鑽 (drill) — 機械/疆域:衝鋒單發 + 沉默波 → 衝鋒高速 + 震波 + 環 ─────────
const DRILL: BossDef = {
  id: 'drill',
  displayName: '鑽地・震波鑽',
  hp: 1400,
  speedPxPerSec: 40,
  size: 162,
  color: 0x5d4037,
  assetKey: 'boss-drill',
  shape: 'diamond',
  faction: 'techno',
  affinityFaction: 'dominion',
  tier: 2,
  attackACooldownMs: 2500,
  attackBCooldownMs: 4700,
  phase2HpThreshold: 560,
  attackBWarningMs: 1000,
  introDialog: '地動山搖,鑽穿你的防線!',
  arenaTint: 0x1f1610,
  entranceCinematic: 'rise-from-ground',
  deathCinematic: 'fragment-into-pieces',
  // boss-teleport-gimmick:震波鑽「鑽地穿梭」=瞬移最貼題;HP<50% 後每 5s 閃位,難被逼角。
  uniqueGimmick: 'teleport',
  tickAttacks(self: Boss, nowMs: number, playerX: number): BossAttack[] {
    // phase1:高速衝鋒單發 (重鑽) + 範圍沉默波 (短,封技不封移)。
    // phase2 (berserk):衝鋒高速光束 (beam-cast) + 大面積震波 (shockwave AoE,預警圈可躲)
    //   + 全向震波環。beam-cast + shockwave + ring-burst 都是 phase1 沒有的新 kind
    //   (衝鋒 + 震波主題質變)。shockwave = 震波鑽招牌「地動」大範圍,圈外即安全。
    const out: BossAttack[] = [];
    if (self.y < self.def.size / 2) return out;
    const berserk = self.berserkMode;
    if (nowMs - self.lastAttackAMs >= self.effectiveAttackACooldownMs) {
      self.lastAttackAMs = nowMs;
      out.push({ kind: 'single-shot', angleRad: aimAngle(self, playerX), speedPxPerSec: 420, damage: 18 });
      out.push({ kind: 'silence-wave', radiusPx: 180, durationMs: 450 });
    }
    if (berserk && nowMs - self.lastAttackBMs >= self.effectiveAttackBCooldownMs) {
      self.lastAttackBMs = nowMs;
      // 衝鋒:高速貫穿光束 (玩家側移即閃) + 大面積震波 (預警 1200ms 走出圈外即安全)
      // + 全向震波環 (雙層+缺口)。
      out.push({ kind: 'beam-cast', angleRad: aimAngle(self, playerX), speedPxPerSec: 540, damage: 16 });
      out.push({ kind: 'shockwave', radiusPx: EXTRA_SHOCKWAVE_RADIUS, warnMs: EXTRA_SHOCKWAVE_WARN_MS, damage: 16 });
      out.push({ kind: 'ring-burst', count: 18, speedPxPerSec: 155, damage: 8, layers: EXTRA_RING_LAYERS, gapRad: EXTRA_RING_GAP_RAD, rotateDps: EXTRA_RING_ROTATE_DPS });
    }
    return out;
  },
};

// ── 骷髏・大雷主 (skull) — 星算:單發 + 魔法彈 → 大雷射 + 環彈 ──────────────────────
const SKULL: BossDef = {
  id: 'skull',
  displayName: '骷髏・大雷主',
  hp: 1300,
  speedPxPerSec: 36,
  size: 152,
  color: 0x4527a0,
  assetKey: 'boss-skull',
  shape: 'hexagon',
  faction: 'celestial',
  affinityFaction: 'celestial',
  tier: 2,
  attackACooldownMs: 2300,
  attackBCooldownMs: 4500,
  phase2HpThreshold: 520,
  attackBWarningMs: 1000,
  introDialog: '雷霆審判,凡軀灰飛煙滅!',
  arenaTint: 0x140a2a,
  entranceCinematic: 'phase-in',
  deathCinematic: 'crystal-shatter',
  // boss-teleport-gimmick:大雷主「相位閃現」=瞬移貼題 (phase-in 入場);HP<50% 後每 5s 閃位。
  uniqueGimmick: 'teleport',
  tickAttacks(self: Boss, nowMs: number, playerX: number): BossAttack[] {
    // phase1:高速精準單發 + 少量波浪魔法彈 (看波預判閃)。
    // phase2 (berserk):大雷射 (laser-beam 長預警) + 大面積落雷 (shockwave AoE,預警圈可躲)
    //   + 環形雷暴 (ring-burst)。laser-beam + shockwave + ring-burst 都是 phase1 沒有的
    //   新 kind (大雷「雷霆審判」全方位質變)。shockwave = 招牌大範圍落雷,圈外即安全。
    const out: BossAttack[] = [];
    if (self.y < self.def.size / 2) return out;
    const berserk = self.berserkMode;
    if (nowMs - self.lastAttackAMs >= self.effectiveAttackACooldownMs) {
      self.lastAttackAMs = nowMs;
      out.push({ kind: 'single-shot', angleRad: aimAngle(self, playerX), speedPxPerSec: 420, damage: 16 });
      out.push({ kind: 'magic-bolt', count: 3, speedPxPerSec: 210, amplitudePx: 42, periodMs: 920, damage: 9 });
    }
    if (berserk && nowMs - self.lastAttackBMs >= self.effectiveAttackBCooldownMs) {
      self.lastAttackBMs = nowMs;
      // 大雷射:長預警 (700ms) 看清方向先閃,再開火 480ms;角度開火當下鎖定 → 可躲。
      out.push({ kind: 'laser-beam', warnMs: 700, fireMs: 480, thickness: 28, damage: 16 });
      // 大範圍落雷:預警 1200ms 走出圈外即安全 (圓心鎖定不追)。
      out.push({ kind: 'shockwave', radiusPx: EXTRA_SHOCKWAVE_RADIUS, warnMs: EXTRA_SHOCKWAVE_WARN_MS, damage: 16 });
      // 環形雷暴:雙層 + 缺口 + 緩旋,衝缺口安全。
      out.push({ kind: 'ring-burst', count: 20, speedPxPerSec: 150, damage: 8, layers: EXTRA_RING_LAYERS, gapRad: EXTRA_RING_GAP_RAD, rotateDps: EXTRA_RING_ROTATE_DPS });
    }
    return out;
  },
};

// ── 晶魔・折射核 (crystal) — 符文:緩速 + 魔法彈 → 環彈折射 + 沉默 ──────────────────
const CRYSTAL: BossDef = {
  id: 'crystal',
  displayName: '晶魔・折射核',
  hp: 1350,
  speedPxPerSec: 32,
  size: 156,
  color: 0x6a1b9a,
  assetKey: 'boss-crystal',
  shape: 'double-circle',
  faction: 'runic',
  affinityFaction: 'runic',
  tier: 2,
  attackACooldownMs: 2400,
  attackBCooldownMs: 4600,
  phase2HpThreshold: 540,
  attackBWarningMs: 1000,
  introDialog: '光在我面前折射,你無處可逃。',
  arenaTint: 0x1a0a2a,
  entranceCinematic: 'teleport-in',
  deathCinematic: 'crystal-shatter',
  uniqueGimmick: 'phase-shield',
  tickAttacks(self: Boss, nowMs: number, _playerX: number): BossAttack[] {
    // phase1:緩速 (命中才緩,非定身) + 波浪魔法彈 (控場)。
    // phase2 (berserk):環彈折射 — 雙層環形彈幕 (ring-burst,缺口+緩旋,折射主題) + 沉默。
    //   ring-burst + silence-player 都是 phase1 沒有的新 kind (折射核質變)。
    const out: BossAttack[] = [];
    if (self.y < self.def.size / 2) return out;
    const berserk = self.berserkMode;
    if (nowMs - self.lastAttackAMs >= self.effectiveAttackACooldownMs) {
      self.lastAttackAMs = nowMs;
      out.push({ kind: 'slow-player', durationMs: 1400, slowMult: 0.55 });
      out.push({ kind: 'magic-bolt', count: 4, speedPxPerSec: 200, amplitudePx: 46, periodMs: 900, damage: 9 });
    }
    if (berserk && nowMs - self.lastAttackBMs >= self.effectiveAttackBCooldownMs) {
      self.lastAttackBMs = nowMs;
      // 環彈折射:雙層環 + 明顯缺口 + 緩旋,衝缺口安全。
      out.push({ kind: 'ring-burst', count: 20, speedPxPerSec: 150, damage: 8, layers: EXTRA_RING_LAYERS, gapRad: EXTRA_RING_GAP_RAD, rotateDps: EXTRA_RING_ROTATE_DPS });
      // 折射沉默:短沉默封技不封移 (維持可玩)。
      out.push({ kind: 'silence-player', durationMs: 500 });
    }
    return out;
  },
};

/**
 * boss-roster-extra 新魔王池 (6 隻正式魔王)。獨立於 BOSS_POOL / TIER2 / TIER3,
 * 避免動到那三池既有 length / fsm-parity 契約;由 pickRandomBoss / pickBossById /
 * boss-sprite-coverage 透過 import 納入遭遇與守門。
 */
export const BOSS_EXTRA_POOL: BossDef[] = [SPIDER, HOVER, TRAIN, DRILL, SKULL, CRYSTAL];

/** Pick a random extra-roster boss. Pure (seedable) for unit tests. */
export function pickRandomExtraBoss(rand: () => number = Math.random): BossDef {
  return BOSS_EXTRA_POOL[Math.floor(rand() * BOSS_EXTRA_POOL.length)];
}
